import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { admiDocumentGenerationService } from './admi-document-generation.service';
import { admiDataCatalogService } from './admi-data-catalog.service';
import {
    DocumentTemplateDto,
    DocumentTemplateListResponseDto,
    DocumentTemplateSearchDto,
    FormFieldDto,
    FormItemDto,
    FormItemKind,
    DynamicSectionDto,
    UploadTarget,
} from './document-generation.dto';
import { DataCatalogDto } from './data-catalog.dto';
import { generateSimpleID } from 'src/common/generateSimpleID';

/**
 * Recursively assigns a stable `_id` to each form item (and nested section items)
 * if one is not already present. This ensures React keys remain stable when the
 * user edits the `key` field.
 */
function ensureStableIds(items: FormItemDto[]): FormItemDto[] {
    return items.map((item) => {
        const patched = item._id ? item : { ...item, _id: generateSimpleID() };
        if (patched.kind === FormItemKind.SECTION) {
            const section = patched as DynamicSectionDto;
            return { ...section, items: ensureStableIds(section.items) } as FormItemDto;
        }
        return patched;
    });
}

export interface State {
    templates: DocumentTemplateListResponseDto;
    isLoading: boolean;
    template: DocumentTemplateDto;
    dataCatalog: DataCatalogDto[];
}

const initialState: State = {
    templates: {
        content: [],
        size: 0,
        totalElements: 0,
        totalPages: 0,
        last: false,
        first: true,
        number: 0,
        empty: true,
    },
    isLoading: false,
    template: {
        name: '',
        templateUrl: '',
        formSchema: [],
        folderRegex: '',
        fileNameTemplate: '',
        uploadTarget: UploadTarget.GOOGLE_DRIVE,
    },
    dataCatalog: [],
};

export const loadDocumentTemplates = createAsyncThunk(
    'documentGenerationTemplate/loadDocumentTemplates',
    async (search: DocumentTemplateSearchDto, _thunkApi) => {
        const response = await admiDocumentGenerationService.getDocumentTemplates(search);

        return response;
    },
);

export const createDocumentTemplate = createAsyncThunk(
    'documentGenerationTemplate/createDocumentTemplate',
    async (template: DocumentTemplateDto, _thunkApi) => {
        console.debug('documentGenerationTemplate/createDocumentTemplate', template);

        const response = await admiDocumentGenerationService.createDocumentTemplate(template);
        return response;
    },
);

export const updateDocumentTemplate = createAsyncThunk(
    'documentGenerationTemplate/updateDocumentTemplate',
    async (template: DocumentTemplateDto, _thunkApi) => {
        console.debug('documentGenerationTemplate/updateDocumentTemplate', template);

        if (!template.id) {
            throw new Error('Template ID is required for update');
        }

        const response = await admiDocumentGenerationService.updateDocumentTemplate(template);
        return response;
    },
);

export const loadDocumentTemplateById = createAsyncThunk(
    'documentGenerationTemplate/loadDocumentTemplateById',
    async (id: string, _thunkApi) => {
        console.debug('documentGenerationTemplate/loadDocumentTemplateById', id);
        const response = await admiDocumentGenerationService.getDocumentTemplateById(id);

        return response;
    },
);

export const deleteDocumentTemplate = createAsyncThunk(
    'documentGenerationTemplate/deleteDocumentTemplate',
    async (id: string, _thunkApi) => {
        console.debug('documentGenerationTemplate/deleteDocumentTemplate', id);
        await admiDocumentGenerationService.deleteDocumentTemplate(id);
        return id; // Return the ID of the deleted template
    },
);

export const loadDataCatalaog = createAsyncThunk('documentGenerationTemplate/loadDataCatalaog', async (_thunkApi) => {
    console.debug('documentGenerationTemplate/loadDataCatalaog');
    const response = await admiDataCatalogService.getDataCatalog();
    return response;
});

const documentGenerationTemplateSlice = createSlice({
    name: 'documentGenerationTemplate',
    initialState,
    reducers: {
        addItemToTemplate: (state, action: PayloadAction<FormItemDto>) => {
            state.template.formSchema.push(action.payload);
        },
        addFieldToTemplate: (state, action: PayloadAction<FormFieldDto>) => {
            state.template.formSchema.push(action.payload);
        },
        addSectionToTemplate: (state, action: PayloadAction<DynamicSectionDto>) => {
            state.template.formSchema.push(action.payload);
        },
        removeItemFromTemplate(state, action: PayloadAction<FormItemDto>) {
            state.template.formSchema = state.template.formSchema.filter((item) => item.key !== action.payload.key);
        },
        removeFieldFromTemplate(state, action: PayloadAction<FormFieldDto>) {
            state.template.formSchema = state.template.formSchema.filter((item) => item.key !== action.payload.key);
        },

        updateItemOnTemplate(state, action: PayloadAction<{ index: number; item: FormItemDto }>) {
            state.template.formSchema = state.template.formSchema.map((item, index) => {
                if (index === action.payload.index) {
                    return {
                        ...item,
                        ...action.payload.item,
                    };
                }
                return item;
            });
        },
        updateFieldOnTemplate(state, action: PayloadAction<{ index: number; field: FormFieldDto }>) {
            state.template.formSchema = state.template.formSchema.map((item, index) => {
                if (index === action.payload.index) {
                    return {
                        ...item,
                        ...action.payload.field,
                    };
                }
                return item;
            });
        },
        updateSectionOnTemplate(state, action: PayloadAction<{ index: number; section: DynamicSectionDto }>) {
            state.template.formSchema = state.template.formSchema.map((item, index) => {
                if (index === action.payload.index) {
                    return {
                        ...item,
                        ...action.payload.section,
                    };
                }
                return item;
            });
        },
        updateTemplate: (state, action: PayloadAction<DocumentTemplateDto>) => {
            state.template = action.payload;
        },

        removeOptionFromFieldConfig: (state, action: PayloadAction<{ fieldKey: string; optionKey: string }>) => {
            const { fieldKey, optionKey } = action.payload;
            const item = state.template.formSchema.find((f) => f.key === fieldKey) as FormFieldDto | undefined;
            if (item && 'config' in item && item.config.options) {
                item.config.options = item.config.options.filter((option) => option !== optionKey);
            }
        },
        addOptionToFieldConfig: (state, action: PayloadAction<{ fieldKey: string; option: string }>) => {
            const { fieldKey, option } = action.payload;
            const item = state.template.formSchema.find((f) => f.key === fieldKey) as FormFieldDto | undefined;
            if (item && 'config' in item && item.config.options) {
                item.config.options.push(option);
            }
        },
    },
    extraReducers: (builder) => {
        builder.addCase(loadDocumentTemplates.fulfilled, (state, action) => {
            state.templates = action.payload;
            state.isLoading = false;
        });

        builder.addCase(loadDocumentTemplates.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(loadDocumentTemplates.rejected, (state) => {
            state.isLoading = false;
        });
        builder.addCase(createDocumentTemplate.fulfilled, (state, action) => {
            state.template = action.payload;
            state.isLoading = false;
        });
        builder.addCase(createDocumentTemplate.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(createDocumentTemplate.rejected, (state) => {
            state.isLoading = false;
        });
        builder.addCase(loadDocumentTemplateById.fulfilled, (state, action) => {
            state.template = {
                ...action.payload,
                formSchema: ensureStableIds(action.payload.formSchema),
            };
            state.isLoading = false;
        });
        builder.addCase(deleteDocumentTemplate.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(deleteDocumentTemplate.fulfilled, (state) => {
            state.isLoading = false;
        });
        builder.addCase(deleteDocumentTemplate.rejected, (state) => {
            state.isLoading = false;
        });
        builder.addCase(loadDataCatalaog.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(loadDataCatalaog.fulfilled, (state, action) => {
            state.dataCatalog = action.payload;
            state.isLoading = false;
        });
        builder.addCase(loadDataCatalaog.rejected, (state) => {
            state.isLoading = false;
        });
    },
});

export const {
    addItemToTemplate,
    addFieldToTemplate,
    addSectionToTemplate,
    removeItemFromTemplate,
    removeFieldFromTemplate,
    updateItemOnTemplate,
    updateFieldOnTemplate,
    updateSectionOnTemplate,
    updateTemplate,
    addOptionToFieldConfig,
    removeOptionFromFieldConfig,
} = documentGenerationTemplateSlice.actions;

export default documentGenerationTemplateSlice.reducer;
